import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMap from 'highcharts/modules/map';
import worldMap from '@highcharts/map-collection/custom/world.geo.json';
import usAllMap from '@highcharts/map-collection/countries/us/us-all.geo.json';
import proj4 from 'proj4';
import { colors } from '@alixpartners/ui-components';

import {
	ChartWrapper,
	TooltipWrapper,
	TooltipHeader,
	TooltipRowTitle,
	TooltipRowValue,
	TooltipGrid,
	ColorWrapper,
	ColorSymbol,
	TooltipRowPinned,
} from './RegionalMap.styles';
import { MAP_COLORS, MARKER_SIZE, MAX_ZOOM_LEVEL, ZOOM_FACTOR } from './utils';
import { STATES_MAP_CODES } from '../utils';
import { getFormattedSpendWithSymbol } from 'utils/number';
import { renderToString } from 'react-dom/server';

if (typeof window !== 'undefined') {
	window.proj4 = window.proj4 || proj4;
}

HighchartsMap(Highcharts);

const statesMap = {
	USA: usAllMap,
};

export class RegionalMap extends React.Component {
	chart = null;

	componentDidUpdate(prevProps) {
		const { data } = this.props;

		if (data !== prevProps.data) {
			this.drawPies();
		}
	}

	drawPies = () => {
		const { chartRef } = this.props;

		if (
			(!chartRef || !chartRef.current || !chartRef.current.chart) &&
			!this.chart
		) {
			return;
		}

		const { data } = this.props;
		const chart =
			(chartRef && chartRef.current && chartRef.current.chart) || this.chart;
		const values = data.map(item => item.total);
		const maxValue = Math.max(...values);
		const minValue = Math.min(...values);

		if (chart) {
			let centerLat, centerLon;

			data.forEach(location => {
				if (!location.lat && !location.lot) {
					const currentState = chart.series[0].points.find(
						item => item.name === location.name && item.properties
					);

					if (currentState) {
						centerLat = parseFloat(currentState.properties.latitude);
						centerLon = parseFloat(currentState.properties.longitude);
					}
				}

				chart.addSeries(
					{
						type: 'mappie',
						style: { fontFamily: 'Roboto' },
						animation: false,
						name: location.name,
						zIndex: 6,
						opacity: 0.9,
						data: location.data,
						center: {
							lat: location.lat || centerLat,
							lon: location.lon || centerLon,
						},
						sizeFormatter: function() {
							const yAxis = this.chart.yAxis[0];
							const zoomFactor =
								(yAxis.dataMax - yAxis.dataMin) / (yAxis.max - yAxis.min);
							const max = Math.max(
								MARKER_SIZE.max *
									(Math.min(zoomFactor, MAX_ZOOM_LEVEL) / ZOOM_FACTOR),
								MARKER_SIZE.max
							);
							const min = Math.max(
								MARKER_SIZE.min *
									(Math.min(zoomFactor, MAX_ZOOM_LEVEL) / ZOOM_FACTOR),
								MARKER_SIZE.min
							);

							const sizeDelta = max - min;
							const valueDelta = maxValue - minValue;
							const totalValue = location.total;
							const size =
								valueDelta === 0
									? max
									: min + ((totalValue - minValue) * sizeDelta) / valueDelta;

							return size;
						},
					},
					false
				);
			});

			chart.redraw();
		}
	};

	tooltipFormat = value => {
		const { currencySymbol } = this.props;

		return getFormattedSpendWithSymbol(value, currencySymbol);
	};

	getOptions = () => {
		const { data, selectedCountry } = this.props;
		const tooltipFormat = this.tooltipFormat;
		const countryWithStatesCode = STATES_MAP_CODES[selectedCountry];

		return {
			chart: {
				map: countryWithStatesCode
					? statesMap[countryWithStatesCode]
					: worldMap,
				borderWidth: 0,
				style: { fontFamily: 'Roboto' },
				height: 280,
				spacingBottom: 0,
				spacingTop: 0,
				events: {
					click: () => this.props.onSelect(null),
				},
				animation: false,
			},
			title: {
				text: '',
			},
			exporting: { enabled: false },
			credits: {
				enabled: false,
			},
			legend: {
				enabled: false,
			},
			mapNavigation: {
				enabled: true,
				buttonOptions: {
					verticalAlign: 'middle',
				},
			},
			tooltip: {
				useHTML: true,
				backgroundColor: colors.white,
				borderWidth: 0,
				borderRadius: 2,
				formatter: function() {
					const { point, series } = this;
					if (!series || !series.name || series.name === 'map') {
						return false;
					}

					return renderToString(
						<TooltipWrapper>
							<TooltipHeader>{series.name}</TooltipHeader>
							<TooltipRowPinned>
								<div />
								<TooltipRowTitle>Total spend</TooltipRowTitle>
								<TooltipRowValue>{tooltipFormat(point.total)}</TooltipRowValue>
							</TooltipRowPinned>
							<TooltipGrid>
								{series.data.map(pointData => (
									<React.Fragment key={pointData.name}>
										<ColorWrapper>
											<ColorSymbol color={pointData.color} />
										</ColorWrapper>
										<TooltipRowTitle>{pointData.name}</TooltipRowTitle>
										<TooltipRowValue>
											{pointData.y
												? `${tooltipFormat(pointData.y)} (${Math.round(
														(pointData.y / point.total) * 100
												  )}%)`
												: '-'}
											{}
										</TooltipRowValue>
									</React.Fragment>
								))}
							</TooltipGrid>
						</TooltipWrapper>
					);
				},
			},
			series: [
				{
					data: [...data],
					joinBy: !countryWithStatesCode
						? ['iso-a3', 'code']
						: ['name', 'name'],
					nullColor: colors.grey2,
					color: MAP_COLORS[0],
					name: 'map',
					borderColor: colors.grey5,
					borderWidth: 0.25,
					cursor: 'pointer',
					states: {
						hover: {
							brightness: 0.05,
							borderWidth: 0.25,
							borderColor: colors.grey8,
						},
					},
					events: {
						click: e => {
							this.props.onSelect(e.point.id);
						},
					},
				},
			],
		};
	};

	onChartInit = chart => {
		this.chart = chart;
		this.drawPies();
	};

	render() {
		const { chartRef } = this.props;

		return (
			<ChartWrapper>
				<HighchartsReact
					highcharts={Highcharts}
					options={this.getOptions()}
					constructorType="mapChart"
					updateArgs={[true, true, false]}
					callback={this.onChartInit}
					ref={chartRef}
				/>
			</ChartWrapper>
		);
	}
}

// Custom mappie series type
Highcharts.seriesType(
	'mappie',
	'pie',
	{
		center: null, // Can't be array by default anymore
		clip: true, // For map navigation
		states: {
			hover: {
				enabled: false,
			},
			inactive: {
				enabled: false,
			},
		},
		dataLabels: {
			enabled: false,
		},
	},
	{
		getCenter: function() {
			const options = this.options;
			const chart = this.chart;
			const slicingRoom = 2 * (options.slicedOffset || 0);

			if (!options.center) {
				options.center = [null, null]; // Do the default here instead
			}

			// Handle lat/lon support
			if (options.center.lat !== undefined) {
				const point = chart.fromLatLonToPoint(options.center);

				options.center = [
					chart.xAxis[0].toPixels(point.x, true),
					chart.yAxis[0].toPixels(point.y, true),
				];
			}

			// Replace lat/lon with plotX/plotY
			if (options.center.plotX !== undefined) {
				options.center = [options.center.plotX, options.center.plotY];
			}

			// Handle dynamic size
			if (options.sizeFormatter) {
				options.size = options.sizeFormatter.call(this);
			}
			// Call parent function
			const result = Highcharts.seriesTypes.pie.prototype.getCenter.call(this);
			// Must correct for slicing room to get exact pixel pos
			result[0] -= slicingRoom;
			result[1] -= slicingRoom;
			return result;
		},
		translate: function(p) {
			this.options.center = this.userOptions.center;
			this.center = this.getCenter();

			return Highcharts.seriesTypes.pie.prototype.translate.call(this, p);
		},
	}
);
